import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./SaleBanner.css";

const SaleBanner = () => {
  const { t } = useTranslation();
  const bannerRef = useRef(null);
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);

  useEffect(() => {
    if (!bannerRef.current || !canvasRef.current) return;

    const banner = bannerRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    
    let isMounted = true;
    let sprites = [];
    const resolution = window.devicePixelRatio || 1;
    let bannerWidth = 0;
    let bannerHeight = 0;
    
    // Set canvas size
    const resizeCanvas = () => {
      const rect = banner.getBoundingClientRect();
      bannerWidth = rect.width || banner.offsetWidth || 0;
      bannerHeight = rect.height || banner.offsetHeight || 0;
      
      // Only resize if we have valid dimensions
      if (bannerWidth > 0 && bannerHeight > 0) {
        // Reset context transform
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        
        canvas.width = bannerWidth * resolution;
        canvas.height = bannerHeight * resolution;
        canvas.style.width = bannerWidth + "px";
        canvas.style.height = bannerHeight + "px";
        
        // Scale context for high DPI displays
        ctx.scale(resolution, resolution);
      }
    };
    
    // Initial resize with a small delay to ensure DOM is ready
    setTimeout(() => {
      resizeCanvas();
    }, 100);
    
    // Also try immediate resize
    resizeCanvas();
    
    // Create Christmas tree shape
    const createTreeShape = (size) => {
      const treeCanvas = document.createElement("canvas");
      const treeCtx = treeCanvas.getContext("2d");
      treeCanvas.width = size * resolution;
      treeCanvas.height = size * resolution;
      treeCtx.scale(resolution, resolution);
      
      const centerX = size / 2;
      const trunkHeight = size * 0.15;
      const trunkWidth = size * 0.2;
      const treeHeight = size - trunkHeight;
      
      // Draw trunk
      treeCtx.fillStyle = "rgba(139, 69, 19, 0.5)";
      treeCtx.fillRect(
        centerX - trunkWidth / 2,
        size - trunkHeight,
        trunkWidth,
        trunkHeight
      );
      
      // Draw tree (triangle layers)
      treeCtx.fillStyle = "rgba(34, 139, 34, 0.5)";
      
      // Bottom layer (largest)
      treeCtx.beginPath();
      treeCtx.moveTo(centerX, trunkHeight);
      treeCtx.lineTo(centerX - size * 0.4, treeHeight);
      treeCtx.lineTo(centerX + size * 0.4, treeHeight);
      treeCtx.closePath();
      treeCtx.fill();
      
      // Middle layer
      treeCtx.beginPath();
      treeCtx.moveTo(centerX, trunkHeight + treeHeight * 0.2);
      treeCtx.lineTo(centerX - size * 0.3, treeHeight - treeHeight * 0.2);
      treeCtx.lineTo(centerX + size * 0.3, treeHeight - treeHeight * 0.2);
      treeCtx.closePath();
      treeCtx.fill();
      
      // Top layer
      treeCtx.beginPath();
      treeCtx.moveTo(centerX, trunkHeight + treeHeight * 0.4);
      treeCtx.lineTo(centerX - size * 0.2, treeHeight - treeHeight * 0.4);
      treeCtx.lineTo(centerX + size * 0.2, treeHeight - treeHeight * 0.4);
      treeCtx.closePath();
      treeCtx.fill();
      
      return treeCanvas;
    };
    
    // Create sprite
    const createSprite = (x, y, isMouse = false) => {
      const width = Math.random() * 30 + 20;
      const height = width;
      const texture = createTreeShape(width);
      
      // Halved velocities for slower movement
      const velocity = isMouse ? (Math.random() * 3 + 1) * 0.5 : (Math.random() * 4 + 2) * 0.5;
      const angle = Math.random() * 360;
      const angleRad = (angle * Math.PI) / 180;
      
      const sprite = {
        texture: texture,
        width: width,
        height: height,
        x: x !== undefined ? x : Math.random() * bannerWidth,
        y: y !== undefined ? y : bannerHeight + 50,
        velocityX: Math.cos(angleRad) * velocity * 0.3,
        velocityY: isMouse ? -Math.abs(Math.sin(angleRad) * velocity * 1.5) : -Math.abs(Math.sin(angleRad) * velocity),
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 1, // Halved rotation speed
        scale: isMouse ? 0.5 : Math.random() * 0.3 + 0.7,
        scaleEnd: isMouse ? 0.3 : Math.random() * 0.3 + 0.5,
        alpha: 0,
        alphaTarget: 0.5,
        fadeInSpeed: isMouse ? 0.1 : 0.02,
        fadeOutSpeed: 0.01,
        gravity: 0.025, // Halved gravity
        life: 0,
        maxLife: isMouse ? 16 : Math.random() * 40 + 30, // Doubled lifetime for slower movement
        originX: 0.5,
        originY: 0.5,
      };
      
      return sprite;
    };
    
    // Create trees on mouse move
    const createMagicTrees = (x, y, count = 2) => {
      const rect = banner.getBoundingClientRect();
      const relativeX = x - rect.left;
      const relativeY = y - rect.top;
      
      for (let i = 0; i < count; i++) {
        const offsetX = (Math.random() - 0.5) * 40;
        const offsetY = (Math.random() - 0.5) * 40;
        sprites.push(createSprite(relativeX + offsetX, relativeY + offsetY, true));
      }
    };
    
    // Initialize sprites (less density - fewer trees)
    const totalSprites = 30; // Reduced from example's 1777
    const addSpriteIfMounted = () => {
      if (isMounted) {
        sprites.push(createSprite());
      }
    };
    for (let i = 0; i < totalSprites && isMounted; i++) {
      setTimeout(addSpriteIfMounted, i * 200); // Stagger creation
    }
    
    // Mouse move handler
    const handleMouseMove = (e) => {
      if (!isMounted) return;
      const rect = banner.getBoundingClientRect();
      const x = e.clientX;
      const y = e.clientY;
      
      // Check if mouse is over banner
      if (
        x >= rect.left &&
        x <= rect.right &&
        y >= rect.top &&
        y <= rect.bottom
      ) {
        createMagicTrees(x, y, 2); // Create 2 trees per mouse move
      }
    };
    
    // Touch move handler for mobile
    const handleTouchMove = (e) => {
      if (!isMounted) return;
      e.preventDefault(); // Prevent scrolling
      const rect = banner.getBoundingClientRect();
      const touch = e.touches[0];
      if (touch) {
        const x = touch.clientX;
        const y = touch.clientY;
        
        // Check if touch is over banner
        if (
          x >= rect.left &&
          x <= rect.right &&
          y >= rect.top &&
          y <= rect.bottom
        ) {
          createMagicTrees(x, y, 2); // Create 2 trees per touch move
        }
      }
    };
    
    // Add mouse and touch listeners to banner
    banner.addEventListener("mousemove", handleMouseMove);
    banner.addEventListener("touchmove", handleTouchMove, { passive: false });
    
    // Animation loop
    const animate = () => {
      if (!isMounted) return;
      
      // Ensure canvas is properly sized
      if (bannerWidth === 0 || bannerHeight === 0) {
        resizeCanvas();
      }
      
      const currentWidth = canvas.width / resolution;
      const currentHeight = canvas.height / resolution;
      
      // Only animate if we have valid dimensions
      if (currentWidth > 0 && currentHeight > 0) {
        ctx.clearRect(0, 0, currentWidth, currentHeight);
      } else {
        // If dimensions are invalid, try to resize and continue
        resizeCanvas();
        animationFrameRef.current = requestAnimationFrame(animate);
        return;
      }
      
      for (let i = sprites.length - 1; i >= 0; i--) {
        const sprite = sprites[i];
        
        sprite.life += 0.1;
        
        // Fade in
        if (sprite.life < 1) {
          sprite.alpha = Math.min(sprite.alpha + sprite.fadeInSpeed, sprite.alphaTarget);
        }
        
        // Fade out near end
        const fadeOutStart = sprite.maxLife * 0.7;
        if (sprite.life > fadeOutStart) {
          const fadeProgress = (sprite.life - fadeOutStart) / (sprite.maxLife - fadeOutStart);
          sprite.alpha = sprite.alphaTarget * (1 - fadeProgress);
        }
        
        // Update position
        sprite.x += sprite.velocityX;
        sprite.y += sprite.velocityY;
        sprite.velocityY += sprite.gravity;
        
        // Update rotation
        sprite.rotation += sprite.rotationSpeed;
        
        // Update scale
        sprite.scale = sprite.scale + (sprite.scaleEnd - sprite.scale) * 0.05;
        
        // Remove if off screen or expired
        if (
          sprite.life > sprite.maxLife ||
          sprite.y < -100 ||
          sprite.x < -100 ||
          sprite.x > bannerWidth + 100
        ) {
          // Recreate sprite at bottom
          sprites[i] = createSprite();
          continue;
        }
        
        // Draw sprite
        if (sprite.alpha > 0) {
          ctx.save();
          
          const offsetX = sprite.originX * sprite.width;
          const offsetY = sprite.originY * sprite.height;
          
          ctx.translate(sprite.x + offsetX, sprite.y + offsetY);
          ctx.rotate((sprite.rotation * Math.PI) / 180);
          ctx.scale(sprite.scale, sprite.scale);
          ctx.globalAlpha = sprite.alpha;
          
          ctx.drawImage(sprite.texture, -offsetX, -offsetY);
          
          ctx.restore();
        }
      }
      
      animationFrameRef.current = requestAnimationFrame(animate);
    };
    
    animate();
    
    // Handle resize with debounce for better mobile performance
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        resizeCanvas();
        // Update sprite positions if banner size changed significantly
        if (sprites.length > 0) {
          const newWidth = banner.getBoundingClientRect().width || banner.offsetWidth || 0;
          const newHeight = banner.getBoundingClientRect().height || banner.offsetHeight || 0;
          if (newWidth > 0 && newHeight > 0) {
            // Adjust sprites that are out of bounds
            sprites.forEach(sprite => {
              if (sprite.x > newWidth) sprite.x = newWidth;
              if (sprite.y > newHeight) sprite.y = newHeight;
            });
          }
        }
      }, 150);
    };
    
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    
    // Also listen for viewport changes on mobile
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", handleResize);
    }
    
    return () => {
      isMounted = false;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      clearTimeout(resizeTimeout);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", handleResize);
      }
      banner.removeEventListener("mousemove", handleMouseMove);
      banner.removeEventListener("touchmove", handleTouchMove);
      sprites = [];
    };
  }, []);

  return (
    <div className="banner-container">
      <div
        className="sale-banner"
        ref={bannerRef}
      >
        <canvas
          ref={canvasRef}
          className="christmas-trees-canvas"
        />
        <div className="sale-text">
          {t("saleBanner.title")}
        </div>
        <div className="tagline">
          {t("saleBanner.tagline")}
        </div>
        <Link to="/loyalty-program" className="cta-button">
          {t("saleBanner.cta")}
        </Link>
      </div>
    </div>
  );
};

export default SaleBanner;
